import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { SPECIALTIES } from "@/data/heallexa";
import { specialtyOverviewPath } from "@/lib/seo-slugs";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/specialties")({
 head: () => {
 const title = "Browse Doctors by Specialty | Heallexa";
 const description =
 "Browse all " + SPECIALTIES.length + " healthcare specialties on Heallexa. Find verified doctors, dentists, therapists, and specialists across the United States — free, no account required.";
 const url = `${SITE}/specialties`;
 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 { name: "twitter:title", content: title },
 { name: "twitter:description", content: description },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: title,
 description,
 url,
 isPartOf: { "@id": `${SITE}/#website` },
 about: { "@type": "Thing", name: "Medical Specialties" },
 breadcrumb: {
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "Specialties", item: url },
 ],
 },
 }),
 },
 ],
 };
 },
 component: SpecialtiesIndex,
});

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function SpecialtiesIndex() {
 const byLetter = LETTERS.map((l) => ({
 letter: l,
 items: SPECIALTIES.filter((s) => s[0].toUpperCase() === l),
 })).filter((g) => g.items.length > 0);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-6xl px-6 py-16">
 <header className="mb-10">
 <p className="text-sm font-medium text-primary mb-2">Heallexa Directory</p>
 <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
 Browse Doctors by Specialty
 </h1>
 <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
 Heallexa covers <strong className="text-foreground font-medium">{SPECIALTIES.length} healthcare specialties</strong> across
 6.3 million verified U.S. providers — from primary care, pediatrics, and dentistry to
 cardiology, psychiatry, and physical therapy. Select a specialty below to find verified
 providers near you, filtered by location, insurance, and availability.
 </p>
 </header>

 <nav aria-label="A Z" className="mb-10 flex flex-wrap gap-2">
 {byLetter.map((g) => (
 <a
 key={g.letter}
 href={`/specialties/${g.letter.toLowerCase()}`}
 className="px-3 py-1.5 rounded-md bg-card border border-border text-sm font-medium hover:bg-accent transition"
 >
 {g.letter}
 </a>
 ))}
 </nav>

 <div className="space-y-10">
 {byLetter.map((g) => (
 <section key={g.letter} id={g.letter}>
 <h2 className="text-xl font-semibold text-foreground mb-3">{g.letter}</h2>
 <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-6">
 {g.items.map((s) => (
 <li key={s}>
 <a
 href={specialtyOverviewPath(s)}
 className="text-sm text-muted-foreground hover:text-primary transition"
 >
 {s}
 </a>
 </li>
 ))}
 </ul>
 </section>
 ))}
 </div>
 </main>
 <FooterSection />
 </div>
 );
}
